import { __awaiter, __generator, __assign } from '../../node_modules/tslib/tslib.es6.js';
import { BrandclubPageType } from '@brandclub/types';
import { styled, useTheme, Grid } from '@mui/material';
import _orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import brandclub_colors from '../../colors/brandclubColors.js';
import { AppButton } from '../../Components/AppButton/index.js';
import { CampaignType } from '../../types/Campaign.js';
import '../../types/filters.js';
import '../../types/product.js';
import '../../types/RewardAmountType.js';
import { trackRetailerProductPageEvent } from '../../utils/analytics.js';
import { getImageAssetUrl } from '../../utils/assetUtils.js';
import { TABLET_SCREEN_SIZE, MOBILE_SCREEN_SIZE, BRANDCLUB_BRAND_ID } from '../../utils/constants/Constants.js';
import { isProductOutOfStock } from '../../utils/productUtils.js';
import { formatMoney, escapeUriPath } from '../../utils/stringUtils.js';
import { BuyBoxDialog } from '../BuyBoxDialog/index.js';

var _a, _b;
var Container = styled('div')({
    '.reward-amount': (_a = {
            fontWeight: 600,
            fontSize: 20,
            letterSpacing: 0.14,
            lineHeight: 1.75
        },
        _a["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            fontSize: 16,
        },
        _a["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            fontSize: 16,
        },
        _a),
    '.reward-desc': (_b = {
            fontSize: 16,
            fontWeight: 500
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            fontSize: 12,
        },
        _b["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            fontSize: 12,
        },
        _b),
});
var getRewardRowProps = function (rewardType) {
    switch (rewardType) {
        case CampaignType.DailyDealsRewardCampaign:
            return { iconUrl: 'instant_campaign_small.svg', subtitleText: 'for purchasing today', buttonText: 'Buy' };
        case CampaignType.PurchaseRewardCampaign:
            return {
                iconUrl: 'purchase_campaign_icon.svg',
                subtitleText: 'for purchasing product',
                buttonText: 'Buy',
            };
        case CampaignType.MembershipRewardCampaign:
            return {
                iconUrl: 'membership_campaign_icon.svg',
                subtitleText: 'for joining the club',
                buttonText: 'Join',
            };
        case CampaignType.SurveyRewardCampaign:
            return {
                iconUrl: 'survey_campaign_icon.svg',
                subtitleText: 'for completing surveys',
                buttonText: 'View',
            };
        case CampaignType.AffiliateRewardCampaign:
            return {
                iconUrl: 'affiliate_campaign_icon.svg',
                subtitleText: 'for referring sales',
                buttonText: 'View',
            };
        case CampaignType.ContentRewardCampaign:
            return { iconUrl: 'content_campaign_icon.svg', subtitleText: 'for viewing content', buttonText: 'View' };
        case CampaignType.ReferralRewardCampaign:
            return {
                iconUrl: 'referral_campaign_small.svg',
                subtitleText: 'for inviting friends to join',
                buttonText: 'View',
            };
        case CampaignType.RetailerConnectRewardCampaign:
            return {
                iconUrl: 'retailer_campaign_small.svg',
                subtitleText: 'for connecting retailers',
                buttonText: 'View',
            };
        default:
            return { iconUrl: '', subtitleText: '', buttonText: 'View' };
    }
};
var trackBuyClick = function (_a) {
    var _b;
    var trackingEventAction = _a.trackingEventAction, retailerId = _a.retailerId, retailPrice = _a.retailPrice, retailerSku = _a.retailerSku, stacklineSku = _a.stacklineSku, retailerProductPageUrl = _a.retailerProductPageUrl, title = _a.title, mainEntity = _a.mainEntity, trackingParams = _a.trackingParams, analyticsTrack = _a.analyticsTrack, availabilityStatus = _a.availabilityStatus;
    if ((mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.type) === 'product' && (stacklineSku || ((_b = mainEntity.entity) === null || _b === void 0 ? void 0 : _b.stacklineSku))) {
        var widgetParams = {
            widgetContext: 'retailerPrices',
            pageType: BrandclubPageType.Product,
            pageId: mainEntity.entity.stacklineSku,
            retailerUrl: retailerProductPageUrl,
            retailerId: retailerId,
            retailerSku: retailerSku,
            stacklineSku: stacklineSku !== null && stacklineSku !== void 0 ? stacklineSku : mainEntity.entity.stacklineSku,
            retailPrice: retailPrice,
            title: title,
            availabilityStatus: availabilityStatus,
        };
        trackRetailerProductPageEvent({
            trackingEventAction: trackingEventAction,
            trackingParams: __assign(__assign({}, trackingParams), widgetParams),
            analyticsTrack: analyticsTrack,
        });
    }
};
var ProductRewardRow = function (_a) {
    var _b;
    var rewardType = _a.rewardType, rewardAmount = _a.rewardAmount, useNavigate = _a.useNavigate, brandEntity = _a.brandEntity, 
    // Link,
    mainEntity = _a.mainEntity, 
    // trackingParams,
    // handleSendText,
    // analyticsTrack,
    subTitle = _a.subTitle, onShareClick = _a.onShareClick, handleJoinClub = _a.handleJoinClub, domainConfig = _a.domainConfig, onAddToCart = _a.onAddToCart, trackingParams = _a.trackingParams, analyticsTrack = _a.analyticsTrack;
    var _c = useMemo(function () {
        return getRewardRowProps(rewardType);
    }, [rewardType]), iconUrl = _c.iconUrl, subtitleText = _c.subtitleText, buttonText = _c.buttonText;
    var theme = useTheme();
    var isCustomBrandPage = (domainConfig === null || domainConfig === void 0 ? void 0 : domainConfig.brandId) != null && domainConfig.brandId !== BRANDCLUB_BRAND_ID;
    var _d = React.useState(undefined), joiningClubStatus = _d[0], setJoiningClubStatus = _d[1];
    var buttonTextToShow = useMemo(function () {
        if (rewardType === CampaignType.MembershipRewardCampaign) {
            if (joiningClubStatus === 'joined') {
                return 'Joined';
            }
            if (joiningClubStatus === 'joining') {
                return 'Joining...';
            }
        }
        return buttonText;
    }, [rewardType, buttonText, joiningClubStatus]);
    var navigate = useNavigate();
    var handleBuyBoxBuyClick = function (item) {
        var trackingEventAction = onAddToCart ? 'addToCart' : 'openRetailerProductPage';
        var isOutOfStock = isProductOutOfStock(item);
        trackBuyClick({
            trackingEventAction: trackingEventAction,
            retailerId: item === null || item === void 0 ? void 0 : item.retailerId,
            retailPrice: item === null || item === void 0 ? void 0 : item.retailPrice,
            retailerSku: item === null || item === void 0 ? void 0 : item.retailerSku,
            stacklineSku: item === null || item === void 0 ? void 0 : item.stacklineSku,
            retailerProductPageUrl: item === null || item === void 0 ? void 0 : item.retailerProductPageUrl,
            title: item === null || item === void 0 ? void 0 : item.title,
            mainEntity: mainEntity,
            trackingParams: trackingParams,
            analyticsTrack: analyticsTrack,
            availabilityStatus: isOutOfStock ? 'OutOfStock' : 'InStock',
        });
        if (onAddToCart) {
            onAddToCart(__assign({}, item));
        }
        else {
            window.open(item.retailerProductPageUrl, '_blank');
        }
    };
    var handleButtonClick = function (e) { return __awaiter(void 0, void 0, void 0, function () {
        var brandPagePath, entity, productsToList_1;
        var _a, _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    brandPagePath = isCustomBrandPage
                        ? ''
                        : "/".concat(escapeUriPath((_b = (_a = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '_'), "/b/").concat((_c = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _c === void 0 ? void 0 : _c.brandId);
                    if (!(rewardType === CampaignType.PurchaseRewardCampaign || rewardType === CampaignType.DailyDealsRewardCampaign)) return [3 /*break*/, 1];
                    entity = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity;
                    productsToList_1 = [
                        __assign({}, entity),
                    ];
                    (_d = entity.matchedProducts) === null || _d === void 0 ? void 0 : _d.forEach(function (matchedProduct) {
                        productsToList_1.push(__assign({}, matchedProduct));
                    });
                    productsToList_1 = _orderBy(productsToList_1, function (p) {
                        return (p.retailPrice || 0) - (p.totalRewardAmount || 0);
                    }, ['asc']);
                    BuyBoxDialog.show({
                        products: productsToList_1,
                        onClick: handleBuyBoxBuyClick,
                        onProductPageClick: function (product) {
                            window.open(product.retailerProductPageUrl, '_blank');
                        },
                    });
                    return [3 /*break*/, 4];
                case 1:
                    if (!(rewardType === CampaignType.MembershipRewardCampaign)) return [3 /*break*/, 3];
                    setJoiningClubStatus('joining');
                    return [4 /*yield*/, (handleJoinClub === null || handleJoinClub === void 0 ? void 0 : handleJoinClub((_e = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _e === void 0 ? void 0 : _e.brandclubId))];
                case 2:
                    _f.sent();
                    setJoiningClubStatus('joined');
                    return [3 /*break*/, 4];
                case 3:
                    if (rewardType === CampaignType.SurveyRewardCampaign) {
                        // open brand survey page
                        navigate(brandPagePath + '/surveys');
                    }
                    else if (rewardType === CampaignType.AffiliateRewardCampaign) {
                        // open share product dialog
                        if (onShareClick) {
                            onShareClick(e, mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity);
                        }
                    }
                    else if (rewardType === CampaignType.ContentRewardCampaign) {
                        // open brand content page
                        navigate(brandPagePath + '/contentrewards');
                    }
                    _f.label = 4;
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, null,
            React.createElement(Grid, { container: true, display: "grid", gap: "20px", gridTemplateColumns: '35px 9fr 90px', alignItems: "center" },
                React.createElement(Grid, { item: true, display: "flex", justifyContent: "center", alignItems: "center" }, iconUrl && (React.createElement("img", { loading: "lazy", src: getImageAssetUrl(iconUrl), alt: "", style: { maxWidth: 35, maxHeight: 35, borderRadius: 0 } }))),
                React.createElement(Grid, { item: true },
                    React.createElement("div", { className: "reward-amount" }, formatMoney(rewardAmount)),
                    React.createElement("div", { className: "reward-desc" }, subTitle !== null && subTitle !== void 0 ? subTitle : subtitleText)),
                React.createElement(Grid, { item: true, sx: {
                        display: 'flex',
                        justifyContent: 'flex-end',
                    } },
                    React.createElement(AppButton, { sx: (_b = {
                                padding: '16px 16px',
                                maxWidth: '100%',
                                width: 82,
                                height: 35,
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: 14,
                                minWidth: '72px',
                                color: theme.palette.primary.main,
                                backgroundColor: brandclub_colors.grayF3F4F5,
                                borderColor: brandclub_colors.grayF3F4F5,
                                fontWeight: 600
                            },
                            _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
                                fontSize: 14,
                                height: 32,
                            },
                            _b["@media screen and (max-width: 400px)"] = {
                                maxWidth: 200,
                            },
                            _b), onClick: handleButtonClick }, buttonTextToShow))))));
};

export { ProductRewardRow };
//# sourceMappingURL=ProductRewardRow.js.map
